import Config from '@Config';
import Network from '@Networks/Localhost';
import FreeRider from '@FreeRider';
/*------------------ Leaderboards ----------------------*/
import { LeaderboardIDs } from '@Config/leaderboards';
import Leaderboard from './Leaderboards/Leaderboard';
import AdsManager from './Ads/AdsManager';

/*-------------------- Players -------------------------*/
import EventEmitter from '@Utils/Events/EventEmitter';
import EventChannels from '@Config/event_channels';
import Score from './Score/Score';
import Devices from '@Config/devices';
import LogError from '@Utils/Logging/LogError';

class Game
{
    /**
     * Phaser game instance
     */
    public phaserGameInstance: Phaser.Game;

    /**
     * Containers
     */
    public gameContainer: HTMLElement;

    public adsPlayer: HTMLVideoElement;

    /**
     * Scores
     */
    public runScore: Score;
    public lifetimeScore: Score;
    public sessionScore: Score;

    /**
     * Leaderboards
     */
    public leaderboardSupport: boolean;
    public leaderboards: {
        [key: string]: Leaderboard
    }

    /**
     * Freerider instance
     */
    public freerider: FreeRider

    /**
     * Ads manager
     */
    public adsManager: AdsManager;

    /**
     * Constructor
     */
    constructor()
    {
        this.freerider = new FreeRider();

        // Craete scores
        this.runScore = new Score();
        this.lifetimeScore = new Score();
        this.sessionScore = new Score();

        this.adsManager = new AdsManager();
        this.leaderboards = {}
        this.leaderboardSupport = false;
    }

    /**
     * Boots the game
     * - This will initialize phaser and start the loading process
     */
    public async boot(Scenes: { new(): Phaser.Scene }[])
    {
        if (Config.DEVICE === Devices.DESKTOP)
        {
            document.body.className += ' ' + 'desktop';
        }

        /**
         * We're going to hard code the required element container
         */
        const gameContainer = document.getElementById('game-container')
        const phaserGameCanvasContainer = document.getElementById('phaser-canvas-container');
        const freeriderGameCanvasContainer = document.getElementById('freerider-canvas-container');
        const adsPlayer = document.getElementById('ads-player') as HTMLVideoElement;

        /**
         * Initialize async
         */
        await Network.initializeAsync();

        /**
         * Initialize free rider with canvas
         */
        this.freerider.initialize(freeriderGameCanvasContainer);

        /**
         * Create Phaser Game
         */
        const phaserGame = new Phaser.Game({
            type: Phaser.AUTO,
            width: Config.WIDTH,
            height: Config.HEIGHT,
            scene: Scenes,
            disableContextMenu: Config.ENVIRONMENT !== 'development',
            parent: phaserGameCanvasContainer,
            transparent: true,
            title: 'Free Rider Jumps ',
            banner: {
                text: '#FFFFFF',
                background: [
                    '#58E5B7',
                    '#4CC9D8',
                    '#4DBEEB',
                    '#2B3B47'
                ],
                hidePhaser: true
            }
        });

        this.phaserGameInstance = phaserGame;
        this.gameContainer = gameContainer;
        this.adsPlayer = adsPlayer;

        window.onresize = this.setGameSize.bind(this);
        this.setGameSize();
    }

    /**
     * Sets loading progress
     * @param progress
     */
    public setLoadingProgress(progress: number)
    {
        Network.setLoadingProgress(progress);
    }

    /**
     * Starts the game and initializes leaderboards
     */
    public async startGame()
    {
        try
        {
            await Network.startGameAsync();

            await Promise.all([
                this.initializeLeaderboardsAsync(),
                this.initializeBestScores()
            ]);

            this.initializeAds();

            Network.onPause(this.onPause.bind(this));

            this.setGameSize();
        }
        catch (error)
        {
            Network.quit();
        }
    }

    /**
     * Initializes ads
     */
    public initializeAds()
    {
        if (this.adsManager.hasInterstitialSupport())
        {
            this.adsManager.loadInterstitialVideo();
        }

        if (this.adsManager.hasRewardedVideoSupport())
        {
            this.adsManager.loadRewardedVideo();
        }
    }

    /**
     * Initializes best scores
     */
    public async initializeBestScores()
    {
        const networkData = await Network.player.getDataAsync(['bestScore']) as Networks.DataObject

        if (networkData.bestScore)
        {
            const lifetimeScore = networkData.bestScore;
            this.lifetimeScore.setDistanceScore(lifetimeScore.distance);
            this.lifetimeScore.setTrickScore(lifetimeScore.tricks);
        }
    }

    /**
     * Initializes Leaderboards
     */
    public async initializeLeaderboardsAsync()
    {
        if (Network.getSupportedAPIs().indexOf('getLeaderboardAsync') !== -1)
        {
            const globalLeaderboard = new Leaderboard(LeaderboardIDs.GLOBAL);
            const weeklyLeaderboard = new Leaderboard(LeaderboardIDs.WEEKLY);
            const contextualLeaderboard = new Leaderboard(LeaderboardIDs.CONTEXTUAL, true);

            this.leaderboards[LeaderboardIDs.GLOBAL] = globalLeaderboard;
            this.leaderboards[LeaderboardIDs.WEEKLY] = weeklyLeaderboard;
            this.leaderboards[LeaderboardIDs.CONTEXTUAL] = contextualLeaderboard;

            await globalLeaderboard.initializeAsync();
            await weeklyLeaderboard.initializeAsync();

            if (Network.context.getID())
            {
                await contextualLeaderboard.initializeAsync();
            }

            this.leaderboardSupport = true;
        }

        return;
    }

    /**
     * Sets game size
     */
    public setGameSize()
    {
        const canvas = this.phaserGameInstance.canvas;

        const gameContainer = this.gameContainer;

        const gameWidth = canvas.width;
        const gameHeight = canvas.height;

        const windowWidth = gameContainer.clientWidth;
        const windowHeight = gameContainer.clientHeight;

        const aspectRatio = gameWidth / gameHeight;

        let newWidth = windowHeight * aspectRatio;
        let newHeight = windowHeight;

        if (newWidth > windowWidth)
        {
            newWidth = windowWidth;
            newHeight = windowWidth / aspectRatio;
        }

        newHeight = Math.floor(newHeight);
        newWidth = Math.floor(newWidth);

        const canvasStyleHeight = `${newHeight}px`;
        const canvasStyleWidth = `${newWidth}px`;

        canvas.style.height = canvasStyleHeight;
        canvas.style.width = canvasStyleWidth;

        if( this.adsPlayer )
        {
            this.adsPlayer.width = newWidth;
            this.adsPlayer.height = newHeight;
        }

        this.freerider.setSize(gameWidth, gameHeight, canvasStyleWidth, canvasStyleHeight);;
    }

    /**
     * On pause
     */
    public onPause()
    {
        EventEmitter.emit(EventChannels.GAME, 'paused');
    }

}

export default new Game();